import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { Outlet, useBlocker } from 'react-router-dom'

import { cancelActiveUploads, hasActiveUploads } from '@Utils/S3Storage'

interface NavigationPrompt {
  readonly message: string
  readonly when: boolean
}

type NavigationPromptReader = () => NavigationPrompt
type RegisterNavigationPrompt = (reader: NavigationPromptReader) => () => void

const NavigationPromptContext = createContext<RegisterNavigationPrompt | null>(null)

export const useNavigationPrompt = (prompt: NavigationPrompt): void => {
  const registerPrompt = useContext(NavigationPromptContext)
  const promptRef = useRef(prompt)
  promptRef.current = prompt

  useEffect(() => {
    if (!registerPrompt) {
      throw new Error('useNavigationPrompt must be used within NavigationGuard')
    }

    return registerPrompt(() => promptRef.current)
  }, [registerPrompt])
}

const NavigationGuard: React.FC = () => {
  const promptReaders = useRef(new Set<NavigationPromptReader>())

  const registerPrompt = useCallback<RegisterNavigationPrompt>(reader => {
    promptReaders.current.add(reader)

    return () => {
      promptReaders.current.delete(reader)
    }
  }, [])

  const activePromptMessages = useCallback(() => {
    return Array.from(promptReaders.current)
      .map(readPrompt => readPrompt())
      .filter(prompt => prompt.when)
      .map(prompt => prompt.message)
      .filter((message, index, messages) => messages.indexOf(message) === index)
  }, [])

  const shouldBlockNavigation = useCallback(() => {
    return hasActiveUploads() || activePromptMessages().length > 0
  }, [activePromptMessages])

  const blocker = useBlocker(shouldBlockNavigation)
  const handlingBlockedNavigation = useRef(false)

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      handlingBlockedNavigation.current = false
      return
    }

    if (handlingBlockedNavigation.current) { return }
    handlingBlockedNavigation.current = true

    const messages = activePromptMessages()
    if (messages.length > 0 && !window.confirm(messages.join('\n\n'))) {
      blocker.reset()
      return
    }

    if (!hasActiveUploads()) {
      blocker.proceed()
      return
    }

    void cancelActiveUploads().then(() => blocker.proceed())
  }, [activePromptMessages, blocker])

  return (
    <NavigationPromptContext.Provider value={registerPrompt}>
      <Outlet />
    </NavigationPromptContext.Provider>
  )
}

export default NavigationGuard
